import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playClick, playSuccess, playSearch } from '../utils/SoundManager';
import { VerifiedBadge, isPremiumMember } from '../components/VerifiedBadge';
import HeaderNotifications from '../components/HeaderNotifications';
import { MultiSelectChips, MultiSelectDropdown, MultiTagInput } from '../components/MultiSelect';
import OnlineStatus from '../components/OnlineStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FILTERS = {
  name: '',
  age_min: '', age_max: '',
  height_min: '', height_max: '',
  weight_min: '', weight_max: '',
  nationality: [], country: [], city: '',
  marriage_type: [], marital_status: [],
  has_children: '', children_with_me: '',
  job: [], job_category: [], job_specialization: '',
  employment_status: '', education_level: [],
  prayer_level: [],
  skin_color: [], health_status: [], smoker: []
};

const JOB_CATEGORIES = [
  { value: 'طبي', label: '🏥 المجال الطبي' },
  { value: 'تعليمي', label: '📚 المجال التعليمي' },
  { value: 'هندسي', label: '⚙️ المجال الهندسي' },
  { value: 'تقني', label: '💻 المجال التقني' },
  { value: 'إداري', label: '🏢 المجال الإداري' },
  { value: 'قانوني', label: '⚖️ المجال القانوني' },
  { value: 'أمني', label: '🛡️ المجال الأمني' },
  { value: 'تجاري', label: '🏪 المجال التجاري' },
  { value: 'حرفي', label: '🔧 المجال الحرفي' },
  { value: 'إعلامي', label: '📺 المجال الإعلامي' },
  { value: 'آخر', label: '📋 مجال آخر' }
];

const COUNTRIES = ['السعودية', 'الإمارات', 'الكويت', 'قطر', 'البحرين', 'عُمان', 'مصر', 'الأردن', 'سوريا', 'العراق', 'اليمن', 'فلسطين', 'لبنان', 'السودان', 'المغرب', 'الجزائر', 'تونس', 'ليبيا'];

// Section قابل للطي
const AccordionSection = ({ title, icon, isOpen, onToggle, children }) => (
  <div
    style={{
      marginBottom: '12px',
      borderRadius: '14px',
      overflow: 'hidden',
      border: '1px solid var(--border-color)',
      background: 'var(--card-bg)',
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 18px', background: isOpen ? 'rgba(0, 137, 123, 0.06)' : 'transparent',
        border: 'none', cursor: 'pointer', textAlign: 'right',
        fontSize: '15px', fontWeight: '700', color: 'var(--text-dark)',
        transition: 'background 0.2s'
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '20px' }}>{icon}</span>
        {title}
      </span>
      <span style={{ fontSize: '18px', color: 'var(--primary)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
        ⌄
      </span>
    </button>
    {isOpen && (
      <div style={{ padding: '18px', borderTop: '1px solid var(--border-color)' }}>
        {children}
      </div>
    )}
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{ 
      display: 'block', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: 'var(--text-dark)', 
      marginBottom: '8px' 
    }}>
      {label}
    </label>
    {children}
  </div>
);

const RangeField = ({ label, valueMin, valueMax, onChangeMin, onChangeMax, suffix = '', placeholderMin = 'من', placeholderMax = 'إلى', testidPrefix = '' }) => (
  <Field label={label}>
    <div style={{ display: 'flex', gap: '10px' }}>
      <input
        data-testid={`${testidPrefix}-min`}
        type="number" className="input" placeholder={placeholderMin}
        value={valueMin} onChange={onChangeMin}
        style={{ flex: 1 }}
      />
      <input
        data-testid={`${testidPrefix}-max`}
        type="number" className="input" placeholder={placeholderMax}
        value={valueMax} onChange={onChangeMax}
        style={{ flex: 1 }}
      />
      {suffix && (
        <span style={{ alignSelf: 'center', fontSize: '12px', color: 'var(--text-light)', minWidth: '30px' }}>
          {suffix}
        </span>
      )}
    </div>
  </Field>
);

function SearchPage() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [mode, setMode] = useState('quick'); // 'name' | 'quick' | 'advanced'
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [openSections, setOpenSections] = useState({
    location: true, age_body: true, appearance: false, marriage: false, work: false, religion: false, health: false
  });

  const setF = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setCurrentUser(r.data);
      runSearch({}, r.data);
    } catch (e) {
      console.error(e);
    }
  };

  // البحث مع تجاهل الحقول الفارغة
  const runSearch = async (overrides = {}, user = currentUser) => {
    if (!user) return;
    setLoading(true);
    try {
      const oppositeGender = user.gender === 'male' ? 'female' : 'male';
      const sentFilters = { ...filters, ...overrides, gender: oppositeGender };
      // إزالة الحقول الفارغة (تشمل المصفوفات الفارغة)
      Object.keys(sentFilters).forEach(k => {
        const v = sentFilters[k];
        if (v === '' || v === null || (Array.isArray(v) && v.length === 0)) delete sentFilters[k];
      });
      const r = await axios.post(`${BACKEND_URL}/api/search`, sentFilters, { withCredentials: true });
      setProfiles(r.data.profiles || []);
      if ((r.data.profiles || []).length > 0) playSuccess();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // عدّ عدد الفلاتر المُفعّلة (لإظهار التحذير عند زيادتها)
  const activeFiltersCount = Object.keys(filters).reduce((acc, k) => {
    const v = filters[k];
    if (k === 'name') return acc; // البحث بالاسم لا يُحسب كفلتر تخصيص
    if (Array.isArray(v) && v.length > 0) return acc + 1;
    if (typeof v === 'string' && v.trim() !== '') return acc + 1;
    return acc;
  }, 0);

  // التبديل في حقل لون البشرة (multi-select بحد أقصى 2)
  const toggleSkinColor = (color) => {
    setFilters(prev => {
      const current = prev.skin_color || [];
      if (current.includes(color)) {
        return { ...prev, skin_color: current.filter(c => c !== color) };
      }
      if (current.length >= 2) {
        // استبدل الأول بالجديد
        return { ...prev, skin_color: [current[1], color] };
      }
      return { ...prev, skin_color: [...current, color] };
    });
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    playSearch();
    runSearch();
  };

  const clearFilters = () => {
    playClick();
    setFilters(INITIAL_FILTERS);
  };

  const switchMode = (newMode) => {
    playClick();
    setMode(newMode);
  };

  const inputStyle = { width: '100%' };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      <div className="header">
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>💍 وِداد</h1>
          <div className="nav-menu" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HeaderNotifications />
            <button className="nav-link" onClick={() => navigate('/dashboard')}>الرئيسية</button>
            <button className="nav-link" style={{ background: 'var(--light-bg)', color: 'var(--primary)', borderRadius: '20px' }}>البحث</button>
            <button className="nav-link" onClick={() => navigate('/messages')}>الرسائل</button>
            <button className="nav-link" onClick={() => navigate('/favorites')}>⭐ الاهتمام</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '24px', paddingBottom: '60px', maxWidth: '880px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div className="floating" style={{ fontSize: '48px', marginBottom: '8px' }}>🔍</div>
          <h1 style={{ fontSize: '24px', color: 'var(--text-dark)', marginBottom: '6px' }}>ابحث عن شريك حياتك</h1>
          <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
            {currentUser?.gender === 'male' ? '👩 البحث في ملفات النساء' : '👨 البحث في ملفات الرجال'}
          </p>
        </div>

        {/* Mode Switcher (3 Tabs) */}
        <div className="card" style={{ padding: '6px', borderRadius: '16px', marginBottom: '18px', display: 'flex', gap: '4px' }}>
          {[
            { key: 'name', label: '🔤 بحث بالاسم', testid: 'mode-name' },
            { key: 'quick', label: '⚡ بحث سريع', testid: 'mode-quick' },
            { key: 'advanced', label: '🎯 بحث متقدم', testid: 'mode-advanced' },
          ].map(t => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => switchMode(t.key)}
              style={{
                flex: 1, padding: '12px 8px', borderRadius: '12px', border: 'none',
                cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                background: mode === t.key ? 'linear-gradient(135deg, var(--primary), var(--purple))' : 'transparent',
                color: mode === t.key ? 'white' : 'var(--text-dark)',
                transition: 'all 0.25s ease'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} className="card fade-in" style={{ padding: '20px', borderRadius: '20px', marginBottom: '20px' }}>
          
          {/* ============ Mode 1: Search by Name ============ */}
          {mode === 'name' && (
            <div>
              <Field label="🔤 اسم العضو">
                <input
                  data-testid="search-name-input"
                  type="text" className="input"
                  placeholder="اكتب اسم العضو أو جزء منه..."
                  value={filters.name}
                  onChange={(e) => setF('name', e.target.value)}
                  autoFocus
                  style={inputStyle}
                />
              </Field>
              <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px', marginBottom: '14px' }}>
                💡 ابحث عن أي عضو بكتابة اسمه أو جزء من اسمه
              </p>
            </div>
          )}

          {/* ============ Mode 2: Quick Search ============ */}
          {mode === 'quick' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                <RangeField
                  label="🎂 العمر"
                  testidPrefix="quick-age"
                  valueMin={filters.age_min} valueMax={filters.age_max}
                  onChangeMin={(e) => setF('age_min', e.target.value)}
                  onChangeMax={(e) => setF('age_max', e.target.value)}
                  suffix="سنة"
                />
                <Field label="📍 الدولة (يمكن اختيار عدة)">
                  <MultiSelectDropdown
                    options={COUNTRIES}
                    value={filters.country}
                    onChange={(v) => setF('country', v)}
                    placeholder="اختر الدول..."
                    testid="quick-country"
                  />
                </Field>
                <Field label="🏙️ المدينة">
                  <input
                    data-testid="quick-city"
                    type="text" className="input"
                    placeholder="مثال: الرياض"
                    value={filters.city}
                    onChange={(e) => setF('city', e.target.value)}
                  />
                </Field>
              </div>
              <Field label="💑 الحالة الاجتماعية (يمكن اختيار عدة)">
                <MultiSelectChips
                  options={[
                    { value: 'أعزب', label: 'أعزب/عزباء' },
                    { value: 'مطلق', label: 'مطلّق/مطلّقة' },
                    { value: 'أرمل', label: 'أرمل/أرملة' },
                    { value: 'متزوج', label: 'متزوج (للتعدد)' }
                  ]}
                  value={filters.marital_status}
                  onChange={(v) => setF('marital_status', v)}
                  testidPrefix="quick-marital"
                />
              </Field>
            </div>
          )}

          {/* ============ Mode 3: Advanced Search ============ */}
          {mode === 'advanced' && (
            <div>
              {/* القسم 1: الجنسية والإقامة */}
              <AccordionSection
                title="الجنسية والإقامة"
                icon="🌍"
                isOpen={openSections.location}
                onToggle={() => toggleSection('location')}
              >
                <Field label="الجنسية (يمكنك اختيار عدة)">
                  <MultiSelectDropdown
                    options={COUNTRIES}
                    value={filters.nationality}
                    onChange={(v) => setF('nationality', v)}
                    placeholder="مثال: السعودية، الإمارات"
                    testid="adv-nationality"
                  />
                </Field>
                <Field label="دولة الإقامة (يمكنك اختيار عدة)">
                  <MultiSelectDropdown
                    options={COUNTRIES}
                    value={filters.country}
                    onChange={(v) => setF('country', v)}
                    placeholder="اختر الدول..."
                    testid="adv-country"
                  />
                </Field>
                <Field label="مدينة السكن">
                  <input
                    data-testid="adv-city"
                    type="text" className="input"
                    placeholder="اكتب اسم المدينة"
                    value={filters.city}
                    onChange={(e) => setF('city', e.target.value)}
                  />
                </Field>
              </AccordionSection>

              {/* القسم 2: العمر والمواصفات الجسدية */}
              <AccordionSection
                title="العمر والطول والوزن"
                icon="📏"
                isOpen={openSections.age_body}
                onToggle={() => toggleSection('age_body')}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                  <RangeField
                    label="العمر" testidPrefix="adv-age"
                    valueMin={filters.age_min} valueMax={filters.age_max}
                    onChangeMin={(e) => setF('age_min', e.target.value)}
                    onChangeMax={(e) => setF('age_max', e.target.value)}
                    suffix="سنة"
                  />
                  <RangeField
                    label="الطول" testidPrefix="adv-height"
                    valueMin={filters.height_min} valueMax={filters.height_max}
                    onChangeMin={(e) => setF('height_min', e.target.value)}
                    onChangeMax={(e) => setF('height_max', e.target.value)}
                    suffix="سم"
                  />
                  <RangeField
                    label="الوزن" testidPrefix="adv-weight"
                    valueMin={filters.weight_min} valueMax={filters.weight_max}
                    onChangeMin={(e) => setF('weight_min', e.target.value)}
                    onChangeMax={(e) => setF('weight_max', e.target.value)}
                    suffix="كجم"
                  />
                </div>
              </AccordionSection>

              {/* القسم 3: المظهر الخارجي */}
              <AccordionSection
                title="المظهر الخارجي"
                icon="🎨"
                isOpen={openSections.appearance}
                onToggle={() => toggleSection('appearance')}
              >
                <Field label="لون البشرة (اختياران كحد أقصى)">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {['أبيض', 'قمحي', 'أسمر', 'أسود'].map(color => {
                      const selected = filters.skin_color.includes(color);
                      return (
                        <button
                          key={color}
                          type="button"
                          data-testid={`skin-${color}`}
                          onClick={() => toggleSkinColor(color)}
                          style={{
                            padding: '10px 18px',
                            borderRadius: '12px',
                            border: selected ? '2px solid var(--primary)' : '1px solid var(--border-color)',
                            background: selected ? 'rgba(255, 107, 157, 0.12)' : 'var(--card-bg)',
                            color: selected ? 'var(--primary)' : 'var(--text-dark)',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {selected && <span>✓</span>}
                          {color}
                        </button>
                      );
                    })}
                  </div>
                  {filters.skin_color.length > 0 && (
                    <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '6px' }}>
                      المحدد: {filters.skin_color.join(' و ')}
                    </p>
                  )}
                </Field>
              </AccordionSection>

              {/* القسم 4: نوع الزواج والحالة الاجتماعية */}
              <AccordionSection
                title="نوع الزواج والحالة الاجتماعية"
                icon="💍"
                isOpen={openSections.marriage}
                onToggle={() => toggleSection('marriage')}
              >
                <Field label="نوع الزواج المقبول (يمكن اختيار عدة)">
                  <MultiSelectChips
                    options={[
                      { value: 'أول', label: 'زواج أول' },
                      { value: 'تعدد', label: 'تعدد' },
                      { value: 'مسيار', label: 'مسيار' }
                    ]}
                    value={filters.marriage_type}
                    onChange={(v) => setF('marriage_type', v)}
                    testidPrefix="adv-marriage-type"
                  />
                </Field>
                <Field label="الحالة الاجتماعية (يمكن اختيار عدة)">
                  <MultiSelectChips
                    options={[
                      { value: 'أعزب', label: 'أعزب/عزباء (لم يسبق الزواج)' },
                      { value: 'مطلق', label: 'مطلّق/مطلّقة' },
                      { value: 'أرمل', label: 'أرمل/أرملة' },
                      { value: 'متزوج', label: 'متزوج (للتعدد)' }
                    ]}
                    value={filters.marital_status}
                    onChange={(v) => setF('marital_status', v)}
                    testidPrefix="adv-marital"
                  />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                  <Field label="الأطفال">
                    <select data-testid="adv-has-children" className="input" value={filters.has_children} onChange={(e) => setF('has_children', e.target.value)}>
                      <option value="">الكل</option>
                      <option value="no_kids">بدون أطفال</option>
                      <option value="with_kids">لديه/لديها أطفال</option>
                    </select>
                  </Field>
                  {filters.has_children === 'with_kids' && (
                    <Field label="مكان إقامة الأطفال">
                      <select data-testid="adv-children-with" className="input" value={filters.children_with_me} onChange={(e) => setF('children_with_me', e.target.value)}>
                        <option value="">لا يهم</option>
                        <option value="yes">الأطفال معه/معها</option>
                        <option value="no">الأطفال ليسوا معه/معها</option>
                        <option value="some">بعضهم فقط معه/معها</option>
                      </select>
                    </Field>
                  )}
                </div>
              </AccordionSection>

              {/* القسم 4: العمل والتعليم */}
              <AccordionSection
                title="العمل والتعليم"
                icon="💼"
                isOpen={openSections.work}
                onToggle={() => toggleSection('work')}
              >
                <Field label="الوظيفة (يمكن إضافة عدة وظائف)">
                  <MultiTagInput
                    value={filters.job}
                    onChange={(v) => setF('job', v)}
                    placeholder="مثال: مهندس، طبيب، معلم"
                    testid="adv-job"
                  />
                </Field>
                <Field label="المجال الوظيفي (يمكن اختيار عدة)">
                  <MultiSelectDropdown
                    options={JOB_CATEGORIES}
                    value={filters.job_category}
                    onChange={(v) => setF('job_category', v)}
                    placeholder="اختر المجالات..."
                    testid="adv-job-cat"
                  />
                </Field>
                <Field label="حالة التوظيف">
                  <select data-testid="adv-emp" className="input" value={filters.employment_status} onChange={(e) => setF('employment_status', e.target.value)}>
                    <option value="">الكل</option>
                    <option value="موظف">موظف</option>
                    <option value="غير موظف">غير موظف</option>
                  </select>
                </Field>
                <Field label="المستوى التعليمي (يمكن اختيار عدة)">
                  <MultiSelectChips
                    options={['ثانوي', 'دبلوم', 'بكالوريوس', 'ماجستير', 'دكتوراه']}
                    value={filters.education_level}
                    onChange={(v) => setF('education_level', v)}
                    testidPrefix="adv-edu"
                  />
                </Field>
              </AccordionSection>

              {/* القسم 5: الالتزام الديني */}
              <AccordionSection
                title="الالتزام الديني"
                icon="🕌"
                isOpen={openSections.religion}
                onToggle={() => toggleSection('religion')}
              >
                <Field label="مستوى الالتزام بالصلاة (يمكن اختيار عدة)">
                  <MultiSelectChips
                    options={['ملتزم في المسجد', 'ملتزم', 'أحياناً', 'نادراً']}
                    value={filters.prayer_level}
                    onChange={(v) => setF('prayer_level', v)}
                    testidPrefix="adv-prayer"
                  />
                </Field>
              </AccordionSection>

              {/* القسم 7: الحالة الصحية والتدخين */}
              <AccordionSection
                title="الحالة الصحية والتدخين"
                icon="🩺"
                isOpen={openSections.health}
                onToggle={() => toggleSection('health')}
              >
                <Field label="الحالة الصحية المقبولة (يمكن اختيار عدة)">
                  <MultiSelectChips
                    options={[
                      { value: 'سليم', label: 'سليم صحياً ✓' },
                      { value: 'مرض مزمن', label: 'مرض مزمن' },
                      { value: 'إعاقة', label: 'إعاقة' },
                      { value: 'إصابة', label: 'إصابة' }
                    ]}
                    value={filters.health_status}
                    onChange={(v) => setF('health_status', v)}
                    testidPrefix="adv-health"
                  />
                </Field>
                <Field label="التدخين (يمكن اختيار عدة)">
                  <MultiSelectChips
                    options={[
                      { value: 'لا', label: 'لا يدخن 🚭' },
                      { value: 'أحياناً', label: 'أحياناً' },
                      { value: 'نعم', label: 'مدخن 🚬' },
                      { value: 'أقلعت', label: 'أقلع عن التدخين' }
                    ]}
                    value={filters.smoker}
                    onChange={(v) => setF('smoker', v)}
                    testidPrefix="adv-smoker"
                  />
                </Field>
                <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '8px' }}>
                  💡 اترك الحقول فارغة لعرض جميع الحالات
                </p>
              </AccordionSection>
            </div>
          )}

          {/* تحذير ذكي عند زيادة التخصيص */}
          {mode === 'advanced' && activeFiltersCount >= 5 && (
            <div
              data-testid="customization-warning"
              style={{
                marginTop: '16px',
                padding: '12px 14px',
                background: 'rgba(255, 165, 0, 0.10)',
                border: '1px solid rgba(255, 165, 0, 0.35)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '13px',
                color: 'var(--text-dark)',
                lineHeight: 1.7
              }}
            >
              <span style={{ fontSize: '22px', flexShrink: 0 }}>💡</span>
              <span>
                <strong>نصيحة:</strong> لقد فعّلت <strong style={{ color: '#FFA500' }}>{activeFiltersCount}</strong> فلاتر.
                كلما زادت دقة المعايير، قلّت احتمالية إيجاد شريك مناسب. حاول أن تكون متوازناً في تخصيصك 🌹
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
            <button
              data-testid="search-submit-btn"
              type="submit"
              className="btn btn-primary btn-glow"
              style={{ flex: 2, padding: '14px', fontSize: '15px', fontWeight: '700' }}
            >
              🔍 ابحث الآن
            </button>
            <button
              data-testid="search-clear-btn"
              type="button"
              onClick={clearFilters}
              className="btn btn-outline"
              style={{ flex: 1, padding: '14px', fontSize: '14px' }}
            >
              🗑️ مسح
            </button>
          </div>
        </form>

        {/* Loading */}
        {loading && (
          <div className="fade-in" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="floating" style={{ fontSize: '50px', marginBottom: '12px' }}>💕</div>
            <p style={{ color: 'var(--text-light)' }}>جاري البحث...</p>
          </div>
        )}

        {/* Empty */}
        {!loading && profiles.length === 0 && (
          <div className="card fade-in-up" style={{ textAlign: 'center', padding: '50px 20px', borderRadius: '20px' }}>
            <div style={{ fontSize: '60px', marginBottom: '14px', opacity: 0.6 }}>🔍</div>
            <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '20px' }}>لا توجد نتائج</h3>
            <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>جرب تعديل معايير البحث</p>
          </div>
        )}

        {/* Results */}
        {!loading && profiles.length > 0 && (
          <div className="fade-in">
            <h3 style={{ color: 'var(--text-dark)', fontSize: '17px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ✨ <span>النتائج ({profiles.length})</span>
            </h3>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
              {profiles.map((p, idx) => (
                <div
                  key={p.user_id}
                  data-testid={`result-${p.user_id}`}
                  className="card profile-card card-float stagger-item"
                  onClick={() => { playClick(); navigate(`/profile/${p.user_id}`); }}
                  style={{ cursor: 'pointer', animationDelay: `${idx * 0.06}s`, borderRadius: '20px', padding: '20px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--primary), var(--purple))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '24px', color: 'white', fontWeight: '700'
                    }}>
                      {p.name?.charAt(0) || '👤'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '17px', fontWeight: '700', color: 'var(--text-dark)',
                        marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'
                      }}>
                        <span>{p.name || 'مستخدم'}</span>
                        {isPremiumMember(p) && <VerifiedBadge size="sm" />}
                      </h3>
                      <p style={{ fontSize: '13px', color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {p.age && <span>🎂 {p.age} سنة</span>}
                        {p.city && <span>📍 {p.city}</span>}
                      </p>
                      <div style={{ marginTop: '8px' }}>
                        <OnlineStatus isOnline={p.is_online} lastSeen={p.last_seen} size="sm" />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                    {p.job && (
                      <span style={{ background: 'rgba(255, 107, 157, 0.13)', color: 'var(--primary)', padding: '5px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: '600' }}>
                        💼 {p.job}
                      </span>
                    )}
                    {p.marital_status && (
                      <span style={{ background: 'rgba(0, 217, 165, 0.13)', color: '#00B388', padding: '5px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: '600' }}>
                        💑 {p.marital_status}
                      </span>
                    )}
                    {p.height && (
                      <span style={{ background: 'rgba(0, 137, 123, 0.13)', color: 'var(--primary)', padding: '5px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: '600' }}>
                        📏 {p.height} سم
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-glow"
                    style={{
                      width: '100%', background: 'linear-gradient(135deg, #00897B, #00B4A2)',
                      borderRadius: '10px', padding: '10px', fontSize: '13px', fontWeight: '600'
                    }}
                  >
                    👁️ عرض الملف الشخصي
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
